<template>
    <v-container class="entrytypeedit">
        <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

        <div class="text-right">
            <v-btn class="ml-auto" @click="convertToSKG()"
                >Auto-Konvertierung auf SKG</v-btn
            >
        </div>

        <h1 class="mt-8">{{ entrytype.name }}</h1>

        <v-row class="mt-8">
            <v-col sm="12" md="6">
                <v-text-field
                    label="Name"
                    v-model="entrytype.name"
                ></v-text-field>
            </v-col>
        </v-row>

        <v-row>
            <v-col sm="12" md="6">
                <v-text-field
                    label="Knowledge Graph Type"
                    v-model="entrytype.type"
                ></v-text-field>
            </v-col>
        </v-row>

        <v-row>
            <v-col sm="12" md="6">
                <v-autocomplete
                    chips
                    deletable-chips
                    multiple
                    :items="getSKGTypes.map((item) => item.fullName)"
                    label="Social Knowledge Graph Type"
                    v-model="entrytype.skgTypes"
                ></v-autocomplete>
            </v-col>
        </v-row>

        <v-row>
            <v-col sm="12" md="6">
                <v-select
                    :menu-props="{ offsetY: true }"
                    v-model="entrytype.templates"
                    :items="getAllTemplates"
                    item-value="_id"
                    Nic
                    :item-text="templateName"
                    label="Templates"
                    multiple
                    chips
                >
                    <template v-slot:selection="{ item, index }">
                        <v-chip>{{ templateName(item) }}</v-chip>
                    </template>
                </v-select>
            </v-col>
        </v-row>

        <v-row>
            <v-col sm="12" md="6">
                <v-checkbox
                    style="max-width: 200px"
                    label="IXEdit deaktivieren"
                    v-model="entrytype.disableIXEdit"
                ></v-checkbox>
            </v-col>
        </v-row>

        <v-row>
            <v-col sm="12">
                <h3>Datenfelder</h3>
            </v-col>
            <PropertyList
                v-if="!loading"
                :props="entrytype.properties"
                ref="properties"
                @update="savingRequired = true"
            />
        </v-row>

        <v-btn
            fab
            x-large
            fixed
            :color="saveColor"
            bottom
            right
            @click="save"
            :disabled="!savingRequired"
        >
            <v-icon v-if="!saving"> save </v-icon>
            <v-progress-circular indeterminate v-if="saving" />
        </v-btn>
    </v-container>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import draggable from 'vuedraggable';
import { mapActions, mapGetters } from 'vuex';
import PropertyList from '../../components/entrytypes/PropertyList';
import _ from 'lodash';
import { SKGModelConversionService } from '@/services/skg-model-conversion.service';

export default {
    name: 'EntryTypeEdit',
    components: {
        PropertyList,
    },
    data() {
        return {
            entrytype: {
                properties: [],
            },
            initialEntryType: '',
            loading: false,
            saving: false,
            savingRequired: false,
        };
    },
    computed: {
        ...mapGetters('templates', { getAllTemplates: 'getAll' }),
        ...mapGetters('datamodel', {
            getSKGProperties: 'getProperties',
            getSKGTypes: 'getTypes',
        }),
        saveColor() {
            if (this.savingRequired) {
                return 'warning';
            } else {
                return 'success';
            }
        },
    },
    methods: {
        ...mapActions('templates', { fetchAllTemplates: 'fetchAll' }),
        ...mapActions('entrytypes', { updateEntryType: 'update' }),
        ...mapActions('datamodel', { fetchSKGDatamodel: 'fetchAll' }),
        templateName: (item) => item.name,
        convertToSKG() {
            this.entrytype.skgTypes = this.entrytype.type
                .split(',')
                .map((t) => t.trim())
                .map((kgType) => {
                    return SKGModelConversionService.getNewTypeName(kgType);
                });

            const convertProps = (props) => {
                return props.map((prop) => {
                    prop.linkNameSKG =
                        SKGModelConversionService.getNewPropertyName(
                            prop.linkName
                        );
                    if (prop.properties?.length > 0) {
                        prop.properties = convertProps(prop.properties);
                    }
                    if (prop.config?.values?.length > 0) {
                        prop.config.values = prop.config.values.map((v) => {
                            return {
                                ...v,
                                valueSKG:
                                    SKGModelConversionService.getNewTermValue(
                                        prop.linkNameSKG,
                                        v.value
                                    ),
                            };
                        });
                    }
                    return prop;
                });
            };

            this.entrytype.properties = convertProps(this.entrytype.properties);

            this.loading = true;
            setTimeout(() => {
                this.loading = false;
            }, 1000);
        },
        async save() {
            this.saving = true;

            this.entrytype.properties = await this.$refs.properties.save();

            this.updateEntryType(this.entrytype)
                .then(() => {
                    this.saving = false;
                    // update version
                    this.entrytype.__v++;
                    // update latest saved version
                    this.initialEntryType = JSON.stringify(this.entrytype);
                    this.savingRequired = false;
                })
                .catch(() => {
                    alert('Speichern fehlgeschlagen.');
                });
        },
    },
    watch: {
        entrytype: {
            handler: function (val, oldVal) {
                if (this.initialEntryType !== JSON.stringify(val)) {
                    this.savingRequired = true;
                } else {
                    this.savingRequired = false;
                }
            },
            deep: true,
        },
    },
    beforeRouteLeave(to, from, next) {
        if (!this.savingRequired) {
            next();
        } else {
            const answer = window.confirm(
                'Alle ungespeicherten Änderungen gehen verloren. Seite verlassen?'
            );
            if (answer) {
                next();
            } else {
                next(false);
            }
        }
    },
    async created() {
        await this.fetchAllTemplates();
        await this.fetchSKGDatamodel();

        this.loading = true;
        const id = this.$router.currentRoute.params.entryTypeID;
        const response = await axios.get(`/api/entrytypes/${id}`);

        this.loading = false;
        this.entrytype = response.data;

        this.initialEntryType = JSON.stringify(response.data);
    },
};
</script>

<style scoped>
.ghost {
    opacity: 0.2;
    background: #c8ebfb;
}

.button {
    margin-top: 35px;
}
.flip-list-move {
    transition: transform 0.5s;
}
.no-move {
    transition: transform 0s;
}
.list-group {
    min-height: 20px;
}
.list-group-item {
    cursor: move;
}
.list-group-item i {
    cursor: pointer;
}
</style>
